import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import NewWidgetTitle, { ALIGN } from '../Utils/Widget/Title/NewWidgetTitle';
import Accordion from './Accordion';
import {
  getAddressTitleAndSubtitle,
  getLatestQuarterPriceInformationData,
} from '../../selectors/selectors';

const sellHouseLink = 'https://www.immobilienscout24.de/wissen/verkaufen/haus-verkaufen.html';
const apartmentLink = 'https://www.immobilienscout24.de/wissen/verkaufen/wohnung-verkaufen.html';
const realEstateSellingAdviceLink = 'https://www.immobilienscout24.de/wissen/verkaufen.html';
const realtorRecommendationLink = 'https://www.immobilienscout24.de/immobilienmakler/';
const realEstateValuationLink = 'https://www.immobilienscout24.de/immobilienbewertung/';

const percentageFormat = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
});

const priceFormat = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const getPercentageChange = (oldValue, newValue) => {
  const value = (100 * (newValue - oldValue)) / oldValue;
  const label = value >= 0 ? 'gestiegen' : 'gefallen';
  return { value: percentageFormat.format(value), label };
};

const getPriceData = (housePricesArray, apartmentPricesArray) => ({
  house: {
    priceOld: priceFormat.format(housePricesArray[0].price),
    priceNow: priceFormat.format(housePricesArray[housePricesArray.length - 1].price),
    percentageDiff: getPercentageChange(housePricesArray[0].price, housePricesArray[housePricesArray.length - 1].price),
    oldYear: housePricesArray[0].year,
    oldQuarter: `Q${housePricesArray[0].quarter} ${housePricesArray[0].year}`,
    label: 'ein Haus',
  },
  apartment: {
    priceOld: priceFormat.format(apartmentPricesArray[0].price),
    priceNow: priceFormat.format(apartmentPricesArray[apartmentPricesArray.length - 1].price),
    percentageDiff: getPercentageChange(
      apartmentPricesArray[0].price,
      apartmentPricesArray[apartmentPricesArray.length - 1].price,
    ),
    oldYear: apartmentPricesArray[0].year,
    oldQuarter: `Q${apartmentPricesArray[0].quarter} ${apartmentPricesArray[0].year}`,
    label: 'eine Eigentumswohnung',
  },
});

const getAccordion2Text = (defaultData, addressTitle) => (
  <>
    Der Quadratmeterpreis für {defaultData.label} in {addressTitle} ist seit {' '}
    <strong className="font-bold">
      <span>{defaultData.oldQuarter}</span>
    </strong>{' '}
    um{' '}
    <strong className="font-bold">
      <span>{defaultData.percentageDiff.value}</span>
      % <span>{defaultData.percentageDiff.label}</span>
    </strong>
    . Kostete der Quadratmeter in{' '}
    <strong className="font-bold">
      <span>{defaultData.oldQuarter}</span>
    </strong>{' '}
    noch durchschnittlich{' '}
    <strong className="font-bold">
      <span>{defaultData.priceOld}</span> €/m²
    </strong>
    , sind es{' '}
    <strong className="font-bold">
      heute <span>{defaultData.priceNow}</span> €/m²
    </strong>
    .
  </>
);

const getAccordion2TextHtml = (defaultData, addressTitle) =>
  ` Der Quadratmeterpreis für ${defaultData.label} in ${addressTitle} ist seit
    <strong>${defaultData.oldQuarter}</strong>
    um
    <strong>
      ${defaultData.percentageDiff.value}
      % ${defaultData.percentageDiff.label}
    </strong>. Kostete der Quadratmeter in
    <strong>${defaultData.oldQuarter}</strong>
    noch durchschnittlich
    <strong>${defaultData.priceOld} €/m²</strong>, sind es
    <strong>heute ${defaultData.priceNow} €/m²</strong>.`;

const getJsonLd = (questionsAndAnswers) => {
  // https://schema.org/FAQPage#jsonld
  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questionsAndAnswers.map((questionAndAnswer) => ({
      '@type': 'Question',
      name: questionAndAnswer.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: questionAndAnswer.answerHtml,
      },
    })),
  };
  return JSON.stringify(data);
};

const Faq = ({
  onToggle,
  addressTitle,
  quarterPriceInformation,
  housePrices,
  apartmentPrices,
}) => {
  if (!apartmentPrices || !housePrices) {
    return null;
  }

  const quarterPriceHouse = quarterPriceInformation.HOUSE_BUY.price;
  const quarterPriceApartment = quarterPriceInformation.APARTMENT_BUY.price;
  const defaultData = getPriceData(housePrices.pricesPerQ, apartmentPrices.pricesPerQ);
  const answer1 = (
    <p>
      Der durchschnittliche Quadratmeterpreis für eine Eigentumswohnung in {addressTitle} liegt aktuell bei{' '}
      <span>{priceFormat.format(quarterPriceApartment)}</span> €. Für Häuser müssen
      Kaufinteressierte im Durchschnitt <span>{priceFormat.format(quarterPriceHouse)}</span> €/m² zahlen.
    </p>
  );
  const answer1Html = `<p>
      Der durchschnittliche Quadratmeterpreis für eine Eigentumswohnung in ${addressTitle} liegt aktuell bei
      ${priceFormat.format(quarterPriceApartment)} €. Für Häuser müssen
      Kaufinteressierte im Durchschnitt ${priceFormat.format(quarterPriceHouse)} €/m² zahlen.
    </p>`;
  const answer2 = (
    <>
      <p>
        <strong className="font-bold">Eigentumswohnungen</strong>:{' '}
        {getAccordion2Text(defaultData.apartment, addressTitle)}
      </p>
      <br />
      <p>
        <strong className="font-bold">Häuser</strong>:{' '}
        {getAccordion2Text(defaultData.house, addressTitle)}
      </p>
    </>
  );

  const answer2Html = `<p>
        <strong>Eigentumswohnungen</strong>:
        ${getAccordion2TextHtml(defaultData.apartment, addressTitle)}
      </p>
      <br />
      <p>
        <strong>Häuser</strong>:
        ${getAccordion2TextHtml(defaultData.house, addressTitle)}
      </p>`;

  const answer3 = (
    <p>
      Die hier angegebenen Preise beziehen sich allgemein auf den Immobilienmarkt in {addressTitle}. Der Wert eines
      Hauses oder einer Wohnung ist aber von vielen verschiedenen Faktoren abhängig. Daher lohnt sich eine individuelle{' '}
      <a className="font-regular link-underline" href={`${realEstateValuationLink}`}>
        Immobilienbewertung
      </a>
      . Hierfür empfehlen wir Ihnen unseren kostenlosen Marktwert-Rechner. Sie erhalten die Marktwert-Einschätzung{' '}
      <strong className="font-bold">sofort</strong>, nachdem Sie alle relevanten Daten eingegeben haben. Ihr
      umfangreiches <strong className="font-bold">Bewertungs-PDF</strong> können Sie{' '}
      <span className="font-bold">in „Meine Immobilien“</span>{' '}
      herunterladen. Zusätzlich bekommen Sie{' '}
      <strong className="font-bold">die wichtigsten Informationen per E-Mail</strong>.
    </p>
  );

  const answer3Html = `<p>
      Die hier angegebenen Preise beziehen sich allgemein auf den Immobilienmarkt in ${addressTitle}.
      Der Wert eines Hauses oder einer Wohnung ist aber von vielen
      verschiedenen Faktoren abhängig. Daher lohnt sich eine
      individuelle <a href="${realEstateValuationLink}">Immobilienbewertung</a>.
      Hierfür empfehlen wir Ihnen unseren kostenlosen Marktwert-Rechner. Sie erhalten die
      Marktwert-Einschätzung <strong>sofort</strong>, nachdem Sie alle relevanten Daten eingegeben haben. Ihr
      umfangreiches <strong>Bewertungs-PDF</strong> können Sie in <strong>„Meine Immobilien“</strong> herunterladen.
      Zusätzlich bekommen Sie <strong>die wichtigsten Informationen per E-Mail</strong>.
    </p>`;

  const answer4 = (
    <p>
      Sie möchten Ihre{' '}
      <a className="font-regular link-underline" href={apartmentLink}>
        Wohnung
      </a>{' '}
      oder Ihr{' '}
      <a className="font-regular link-underline" href={sellHouseLink}>
        Haus verkaufen
      </a>
      ? Obwohl der Verkauf einer Immobilie viel Zeit und Detailwissen erfordert, muss der Ablauf nicht stressig sein. In
      unserem{' '}
      <a className="font-regular link-underline" href={realEstateSellingAdviceLink}>
        Ratgeber für den Immobilienverkauf
      </a>{' '}
      begleiten wir Sie <strong className="font-bold">Schritt für Schritt durch den Verkaufsprozess</strong> und geben
      Ihnen wertvolle Tipps und Hinweise. Alternativ können Sie sich auch eine{' '}
      <strong className="font-bold">
        <a className="font-regular link-underline font-bold" href={realtorRecommendationLink}>
          Maklerempfehlung
        </a>{' '}
        einholen
      </strong>
      . Mit einem Profi an Ihrer Seite sparen Sie Zeit, Nerven und Geld.
    </p>
  );

  const answer4Html = `<p>
      Sie möchten Ihre <a href="${apartmentLink}">Wohnung</a> oder
      Ihr <a href="${sellHouseLink}">Haus verkaufen</a>?
      Obwohl der Verkauf einer Immobilie viel Zeit und Detailwissen erfordert, muss der Ablauf nicht stressig sein.
      In unserem <a href="${realEstateSellingAdviceLink}">Ratgeber für den Immobilienverkauf</a>
      begleiten wir Sie <strong>Schritt für Schritt durch den Verkaufsprozess</strong> und geben
      Ihnen wertvolle Tipps und Hinweise. Alternativ können Sie sich auch eine
      <strong><a href="${realtorRecommendationLink}">Maklerempfehlung</a> einholen</strong>.
      Mit einem Profi an Ihrer Seite sparen Sie Zeit, Nerven und Geld.
    </p>`;

  const cleanHtml = (code) => code.replace(/\n/g, ' ').replace(/\s\s+/g, ' ');

  const questionsAndAnswers = [
    {
      question: `Wie viel kostet ein Quadratmeter in ${addressTitle}?`,
      answer: answer1,
      answerHtml: `${cleanHtml(answer1Html)}`,
    },
    {
      question: `Wie haben sich die Immobilienpreise in ${addressTitle} entwickelt?`,
      answer: answer2,
      answerHtml: `${cleanHtml(answer2Html)}`,
    },
    {
      question: `Was ist meine Immobilie in ${addressTitle} wert?`,
      answer: answer3,
      answerHtml: `${cleanHtml(answer3Html)}`,
    },
    {
      question: `Wie kann ich meine Immobilie in ${addressTitle} verkaufen?`,
      answer: answer4,
      answerHtml: `${cleanHtml(answer4Html)}`,
    },
  ];

  return (
    <div>
      <NewWidgetTitle id="faq" align={ALIGN.left} text="Häufig gestellte Fragen" />
      {questionsAndAnswers.map((data, index) => (
        <Accordion
          extraClassContainer={index === questionsAndAnswers.length - 1 ? 'margin-bottom-none' : ''}
          extraClassPanelContent={index === questionsAndAnswers.length - 1 ? 'padding-bottom-none' : ''}
          key={index}
          id={`faq${index}`}
          onToggle={onToggle}
          title={<h3 className="font-standard font-line-standard margin-none">{data.question}</h3>}
        >
          {data.answer}
        </Accordion>
      ))}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: getJsonLd(questionsAndAnswers),
        }}
      />
    </div>
  );
};

Faq.propTypes = {
  apartmentPrices: PropTypes.object,
  housePrices: PropTypes.object,
  onToggle: PropTypes.func,
  addressTitle: PropTypes.string.isRequired,
  quarterPriceInformation: PropTypes.object.isRequired,
};

Faq.defaultProps = {
  onToggle: () => {},
  apartmentPrices: null,
  housePrices: null,
};

const mapStateToProps = (state) => ({
  addressTitle: getAddressTitleAndSubtitle(state).title,
  quarterPriceInformation: getLatestQuarterPriceInformationData(state),
});

export default connect(mapStateToProps)(Faq);
